import { NavLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import { RouteItem } from '../../../../../interface/RouteItem';
import { useTranslation } from 'react-i18next';
import Title from './Title';
import { SidebarMenu } from '../../../../../enum/SidebarMenu';
import { useAppSelector } from '../../../../../hook/useAppSelector';
import { useAppDispatch } from '../../../../../hook/useAppDispatch';
import { saveConfigToBackend, setActiveSidebarElement } from '../../../../../store/slice/config/config';
import menuArrow from '../../../../../assets/img/svg/sidebar/menu-arrow.svg';
import { ThemeEnum } from '../../../../../enum/Theme';
import { getPlatform, getThemeFromPlatform } from '../../../../../util/platform';
import ThemeToggle from '../../Theme/ThemeToggle';
import TimeZone from '../../Time/TimeZone';
import { toggleFormat } from '../../../../../store/slice/config/config';
import { EU, EU_NUMBER_FORMAT, US, US_NUMBER_FORMAT } from '../../../../../constant/constants';
import { updateApplicationConfig } from '../../../../../util/update-config';

interface Props {
  title: string;
  menuRoutes: RouteItem[];
  icon: string;
  sidebarMenuType: SidebarMenu;
  testId: string;
}

const DropdownMenu: React.FC<Props> = ({ title, menuRoutes, icon, sidebarMenuType, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const open = useRef(false);
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const settingsRef = useRef<HTMLDivElement | null>(null);
  const [menuHeight, setMenuHeight] = useState(0);
  const [settingsHeight, setSettingsHeight] = useState(0);
  const activeSidebarElement = useAppSelector((state) => state.configSlice.activeSidebarElement);
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.configSlice.theme);
  const themesByPlatform = useAppSelector((state) => state.configSlice.themesByPlatform);
  const format = useAppSelector((state) => state.configSlice.format);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      setMenuHeight(menuRef.current.scrollHeight);
    } else {
      setMenuHeight(0);
    }
  }, [isOpen, menuRoutes, isSettingsOpen]);

  useEffect(() => {
    if (isSettingsOpen && settingsRef.current) {
      setSettingsHeight(settingsRef.current.scrollHeight + 500);
    } else {
      setSettingsHeight(0);
    }
  }, [isOpen, menuRoutes, isSettingsOpen]);

  useEffect(() => {
    if (!isOpen) setIsSettingsOpen(false);
  }, [isOpen]);

  useEffect(() => {
    const handler = (): void => {
      if (!open.current) {
        setIsOpen(false);
      }

      open.current = false;
    };

    window.addEventListener('click', handler);

    return () => window.removeEventListener('click', handler);
  }, []);

  const handleMenuClick = (): void => {
    if (!isOpen) {
      open.current = true;
    }

    setIsOpen((prevState) => !prevState);
    dispatch(setActiveSidebarElement(sidebarMenuType));
  };
  const [selectedTheme, setSelectedTheme] = useState<ThemeEnum>(theme);

  useEffect(() => {
    const newSelectedTheme = getThemeFromPlatform(themesByPlatform) || theme;
    setSelectedTheme(newSelectedTheme);
  }, [theme, themesByPlatform]);

  const handleThemeToggle = () => {
    const newTheme = selectedTheme === ThemeEnum.DARK ? ThemeEnum.LIGHT : ThemeEnum.DARK;

    const platform = getPlatform();

    dispatch(
      saveConfigToBackend({
        theme: newTheme,
        themesByPlatform: [{ platform, theme: newTheme }],
      }),
    );
  };

  const getNumberFormatText = (): string => {
    return format === EU ? EU : US;
  };

  return (
    <div
      onClick={handleMenuClick}
      className={`sidebar_element_wrapper ${isOpen ? 'opened' : ''} ${
        activeSidebarElement === sidebarMenuType ? 'active' : ''
      }`}
      data-pw={testId}
    >
      <Title isOpen={isOpen} title={title} icon={icon}></Title>

      <div
        className={`menu ${isOpen ? 'opened' : ''}`}
        onClick={(e) => e.stopPropagation()}
        ref={menuRef}
        style={{
          maxHeight: `${menuHeight + settingsHeight}px`,
          opacity: isOpen ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease, opacity 0.3s ease',
        }}
      >
        <div
          ref={settingsRef}
          className={`settings ${isSettingsOpen ? 'opened' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsSettingsOpen((prev) => !prev);
          }}
        >
          <div className="settings_header">
            <label>{t('sidebar.user.settings')}</label>
            <img className={`arrow_icon ${isSettingsOpen ? 'rotated' : ''}`} src={menuArrow} alt="arrow" />
          </div>
          <div className="settings_content">
            <ThemeToggle theme={selectedTheme} onToggle={handleThemeToggle} />
            <div className="number_format_container" onClick={(e) => e.stopPropagation()}>
              <span className="number_format_header">{t('sidebar.user.numberFormat')}:</span>
              <div className="number_format_content">
                {format === EU ? EU_NUMBER_FORMAT : US_NUMBER_FORMAT}
                <button
                  className="switch"
                  data-format={format}
                  onClick={() => {
                    const newFormat = format === US ? EU : US;
                    dispatch(toggleFormat());
                    updateApplicationConfig({ format: newFormat }, dispatch);
                  }}
                >
                  <span className="thumb" />
                  <span className="text"> {getNumberFormatText()}</span>
                </button>
              </div>
            </div>
            <TimeZone />
          </div>
        </div>

        {menuRoutes?.map((route) => (
          <NavLink
            key={`route.path${route.path}_route.label${route.label}`}
            to={`${route.path}`}
            onClick={route.onClick}
          >
            <p data-pw={route.testId}>{t(`${route.label}`)}</p>
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default DropdownMenu;
